import React, { useEffect, useRef, useState } from 'react';
import VideoRecorderActions from '../../recordv1/VideoRecorderActions';
import useRecorder from '../../recordv1/useRecorder';
import TeleprompterDisplay from '../../recordv1/Teleprompter/TeleprompterDisplay';

import { RecorderBody } from '../../recordv1/RecorderBody';
import { RecordingHeaderWithRouteGuard } from '../../recordv1/RecordingHeaderWithRouteGuard';

import { RECORDING_STATUS } from '../../recordv1/types';
import { RecordCamLoader } from '../../recordv1/RecordCamLoader';
import { useHistory } from 'react-router';
import {
  getAudioSettings,
  getVideoSettings,
} from '../../recordv1/utils/recorderSettings';
import { enableCamera } from '../../recordv1/utils/media';
import { ParagraphExtraSmall } from 'lib/components/styles/typography';
import { FreemiumPreview } from './FreemiumPreview';

interface RecordFreemiumCamProps {
  handleShowGetStartedGuide: () => void;
}

export const RecordFreemiumCam = ({
  handleShowGetStartedGuide,
}: RecordFreemiumCamProps) => {
  const history = useHistory();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const cameraStreamRef = useRef<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);

  const userSettings = {
    audioSource: { label: 'Default', value: 'default' },
    videoSource: { label: 'Default', value: 'default' },
    videoQuality: 'high',
  };

  const audioSettings = getAudioSettings(userSettings);
  const videoSettings = getVideoSettings(userSettings);

  const stopAllStreams = async () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
    }
    setScreenStream(null);
  };

  const inititeStream = async () => {
    const stream = await enableCamera(audioSettings, videoSettings);
    if (stream) {
      cameraStreamRef.current = stream;
      setScreenStream(stream);
    }
  };

  // virtual background state

  const {
    stopRecording,
    pauseRecording,
    startCountDown,
    stopCountDown,
    restartRecording,
    status,
    recordedBlob,
    countdown,
    timeInSeconds,
  } = useRecorder({
    canvasRef,
    videoRef,
    screenStream: screenStream as MediaStream,
    stopAllStreams,
    shouldStartCountdown: false,
  });

  useEffect(() => {
    if (status === RECORDING_STATUS.COUNTDOWN) {
      handleShowGetStartedGuide();
    }
  }, [status, handleShowGetStartedGuide]);

  useEffect(() => {
    if (recordedBlob === null) {
      inititeStream();
    }
    return () => {
      stopAllStreams();
    };
  }, [recordedBlob]);

  const setRerecordHandler = async () => {
    restartRecording();
  };

  if (status === RECORDING_STATUS.DONE && recordedBlob) {
    return (
      <FreemiumPreview
        videoBlob={recordedBlob as any}
        setRerecordHandler={setRerecordHandler}
      />
    );
  }

  return (
    <>
      <ParagraphExtraSmall
        style={{
          textDecoration: 'underline',
          cursor: 'pointer',
          marginBottom: '16px',
          height: '16px',
        }}
        onClick={() => history.push('/')}
      >
        {status === RECORDING_STATUS.RECORDING ||
        status === RECORDING_STATUS.PAUSED
          ? ''
          : 'Skip for now'}
      </ParagraphExtraSmall>
      <RecordingHeaderWithRouteGuard
        status={status}
        handleGoToRecordHome={() => {
          history.push('/home');
        }}
        hideHeader
      />

      <div style={{ position: 'relative' }}>
        {status === RECORDING_STATUS.IDLE && (
          <RecordCamLoader
            text='Loading camera'
            extendStyles={{ wrapper: { height: '340px' } }}
          />
        )}

        <RecorderBody
          videoRef={videoRef}
          canvasRef={canvasRef}
          countdown={countdown}
          status={status}
          teleprompter={<TeleprompterDisplay status={status} />}
          extendStyles={{ bodyWrapper: { height: '340px' } }}
        />
      </div>
      <VideoRecorderActions
        startCountDown={startCountDown}
        stopCountDown={stopCountDown}
        pauseRecording={pauseRecording}
        stopRecording={stopRecording}
        status={status}
        timeInSeconds={timeInSeconds}
        isLoadingVB={false}
      />
    </>
  );
};
